import {
	useSetReferenceCustomersMutation,
	useSubmitReferenceCustomersMutation,
} from "@/pages/Private/redux/onboardingIcp/onboardingIcp.api";

import { ReferenceCustomersResponse } from "../../schema/icp";

import { ReferenceCustomerSeeds } from "./ReferenceCustomerSeeds";
import { ChatScript } from "./useChatScript";

interface CustomerSeedsStepOptions {
	chat: ChatScript;
	/** Translator scoped to `onboarding.chat.`. */
	ts: (key: string) => string;
	/** True once the chat unmounted — every await has to re-check it. */
	isCancelled: () => boolean;
	/** The step is over: the seeds are confirmed, skipped, or none resolved. */
	onDone: () => void;
}

export interface CustomerSeedsStep {
	/** Asks for the best customers and opens the free-text composer. */
	askCustomers: () => void;
	/**
	 * Thin branch only: runs the opener answer through the extraction and, when
	 * it already named companies, shows them instead of asking again. Resolves
	 * `true` when it handled the step.
	 */
	tryReuseFromHint: (hint: string | null) => Promise<boolean>;
	handleSendCustomers: (text: string) => Promise<void>;
}

/**
 * The "who are your best customers?" beat of the onboarding chat (PREC-3653),
 * with the editable seed list PREC-3861 added: what the extraction resolved is
 * shown as chips the user can correct, and the step only ends when they confirm
 * them — the lookalike recommendation is derived from exactly this list.
 */
export const useCustomerSeedsStep = ({
	chat,
	ts,
	isCancelled,
	onDone,
}: CustomerSeedsStepOptions): CustomerSeedsStep => {
	const [submitReferenceCustomers] = useSubmitReferenceCustomersMutation();
	const [setReferenceCustomers] = useSetReferenceCustomersMutation();

	/**
	 * Persists the edited seed list; null keeps the chips the user already sees.
	 * The whole response comes back, not just the customers, because the
	 * mixed-kinds verdict is recomputed on every edit (PREC-3896).
	 */
	const saveSeeds = async (names: string[]): Promise<ReferenceCustomersResponse | null> => {
		try {
			return await setReferenceCustomers({ names }).unwrap();
		} catch (err) {
			console.error(err);

			return null;
		}
	};

	const showSeeds = (response: ReferenceCustomersResponse) => {
		chat.addAi(
			<>
				{ts("customersAck")}
				<ReferenceCustomerSeeds
					customers={response.customers}
					mixed={response.mixed === true}
					onConfirm={onDone}
					onSave={saveSeeds}
				/>
			</>
		);
	};

	const handleSkipCustomers = () => {
		chat.clearChips();
		chat.setInputMode("hidden");
		chat.addUser(ts("chipSkipCustomers"));
		onDone();
	};

	const askCustomers = () => {
		chat.addAi(
			<>
				{ts("customersQuestion")}
				<br />
				<span className="text-xs italic text-text-meta">{ts("customersExample")}</span>
			</>,
			[{ label: ts("chipSkipCustomers"), onClick: handleSkipCustomers }]
		);
		chat.setInputMode("customers");
	};

	const tryReuseFromHint = async (hint: string | null): Promise<boolean> => {
		if (!hint) {
			return false;
		}

		chat.setIsTyping(true);

		try {
			const response = await submitReferenceCustomers({ message: hint }).unwrap();

			if (isCancelled()) {
				return true;
			}

			if (response.customers.length > 0) {
				chat.setIsTyping(false);
				showSeeds(response);

				return true;
			}
		} catch (err) {
			console.error(err);

			if (isCancelled()) {
				return true;
			}
		}

		chat.setIsTyping(false);

		return false;
	};

	const handleSendCustomers = async (text: string) => {
		chat.clearChips();
		chat.addUser(text);
		chat.setInputMode("hidden");
		chat.setIsTyping(true);

		try {
			const response = await submitReferenceCustomers({ message: text }).unwrap();

			if (isCancelled()) {
				return;
			}

			chat.setIsTyping(false);

			if (response.customers.length > 0) {
				showSeeds(response);

				return;
			}

			chat.addAi(<>{ts("customersAckNone")}</>);
		} catch (err) {
			console.error(err);

			if (isCancelled()) {
				return;
			}

			chat.setIsTyping(false);
			chat.addAi(<>{ts("customersAckNone")}</>);
		}

		onDone();
	};

	return { askCustomers, tryReuseFromHint, handleSendCustomers };
};
